import type { FeedbackTally } from "@skills-marketplace/contracts/contract-8-feedback";
import { usefulnessDisplay, usefulnessLabel, usefulnessTooltip } from "../lib/feedback";
import ThumbUpIcon from "../icons/ThumbUpIcon";
import styles from "./UsefulnessBadge.module.css";

interface Props {
  /** The entry's tally, or undefined when it has none. */
  tally: FeedbackTally | undefined;
}

/**
 * Inline "N of M found useful" indicator for catalog cards — issue #617.
 *
 * CARDS ONLY, not list rows. #617's criterion is "an inline 'N of M found useful'
 * badge appears on catalog cards", and the validated prototype shows no usefulness
 * column in list view. An earlier revision invented one; this docstring then claimed
 * list rows for longer than the code ever delivered them (raised on #571). If a list
 * column is ever wanted, it is a product change to #617, not a docs fix.
 *
 * Renders for EVERY entry, including one with no votes at all — #620's criterion is
 * "fewer than 5 total votes", and zero is fewer than five. An earlier revision
 * returned null at zero, following the prototype's card helper; the written criterion
 * overrules it, and a card showing nothing is indistinguishable from a card whose
 * signal failed to load.
 *
 * All copy and the vote-count gate come from lib/feedback so this and the detail
 * block cannot word the same tally differently.
 */
export default function UsefulnessBadge({ tally }: Props) {
  const display = usefulnessDisplay(tally);

  const className =
    display.kind === "rated" ? styles.badge : `${styles.badge} ${styles.insufficient}`;

  return (
    <span className={className} title={usefulnessTooltip(display)}>
      <ThumbUpIcon className={styles.icon} />
      {usefulnessLabel(display)}
    </span>
  );
}
